import { useEffect, useRef, RefObject } from "react";

/**
 * Keyboard repositioning for repositionInputs.
 *
 * Lift: bottom = baselineInnerHeight − vv.height (after scrollTo 0). Baseline survives
 * Safari collapsing live innerHeight.
 * Size: height fills the visual viewport so Body can scroll.
 * Underfill: CSS ::after (absolute, top 100%, 200%) — Popup must stay overflow:visible.
 * Scroll: .ui-bottom-sheet-dialog-scroll wraps all parts; keyboard open makes it the
 * scroller (Footer moves). Header is position:sticky. Focused fields scroll into view.
 */

const KEYBOARD_RESIZE_THRESHOLD = 60;
const WINDOW_TOP_OFFSET = 26;
const TAP_MOVE_THRESHOLD = 10;

const KEYBOARD_INPUT_TYPES = new Set([
  "email",
  "number",
  "password",
  "search",
  "tel",
  "text",
  "url",
]);

type UseVirtualKeyboardParams = {
  enabled: boolean;
  open: boolean;
  mounted: boolean;
  popupRef: RefObject<HTMLDivElement | null>;
  setKeyboardOpen: (open: boolean) => void;
};

function isKeyboardInput(element: EventTarget | null): element is HTMLElement {
  if (!(element instanceof HTMLElement)) {
    return false;
  }
  if (element.isContentEditable) {
    return true;
  }
  if (element instanceof HTMLTextAreaElement) {
    return !element.disabled;
  }
  return (
    element instanceof HTMLInputElement &&
    KEYBOARD_INPUT_TYPES.has(element.type) &&
    !element.disabled
  );
}

function getKeyboardMetrics(baselineInnerHeight: number) {
  const vv = window.visualViewport;
  if (!vv) {
    return null;
  }
  const vvHeight = vv.height;
  const liveHeight = Math.max(0, Math.round(window.innerHeight - vvHeight));
  // Safari often shrinks innerHeight toward vv.height after focus-scroll; baseline
  // (captured when the sheet opened) still reports the real keyboard size.
  const fromBaseline = Math.max(0, Math.round(baselineInnerHeight - vvHeight));
  const keyboardHeight = Math.max(liveHeight, fromBaseline);
  return { vvHeight, keyboardHeight };
}

function isOverflowYScrollIntent(el: HTMLElement): boolean {
  const overflowY = window.getComputedStyle(el).overflowY;
  return overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
}

/** Prefer overflowing scroller; while keyboard open, prefer Body+Footer scroll region. */
function findSheetScrollTarget(start: EventTarget | null, boundary: HTMLElement): HTMLElement | null {
  if (boundary.hasAttribute("data-keyboard-open")) {
    const region = boundary.querySelector(".ui-bottom-sheet-dialog-scroll");
    if (region instanceof HTMLElement) {
      return region;
    }
  }
  let node = start instanceof HTMLElement ? start : null;
  while (node && node !== boundary) {
    if (isOverflowYScrollIntent(node) && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  const body = boundary.querySelector(".ui-bottom-sheet-dialog-body");
  if (body instanceof HTMLElement) {
    return body;
  }
  return null;
}

/**
 * Controlled by Root `repositionInputs` (default true).
 */
export function useVirtualKeyboard({
  enabled,
  open,
  mounted,
  popupRef,
  setKeyboardOpen,
}: UseVirtualKeyboardParams): void {
  const keyboardOpenRef = useRef(false);
  const baselineInnerHeightRef = useRef(0);
  const lastAppliedRef = useRef<{ bottom: number; height: number; vvHeight: number } | null>(null);
  const settleTimersRef = useRef<number[]>([]);
  const tapStartRef = useRef<{ x: number; y: number } | null>(null);
  const gestureMovedRef = useRef(false);
  const scrollTouchRef = useRef<{ scrollable: HTMLElement | null; lastY: number } | null>(null);

  useEffect(() => {
    if (!enabled || !mounted || !open) {
      keyboardOpenRef.current = false;
      baselineInnerHeightRef.current = 0;
      lastAppliedRef.current = null;
      setKeyboardOpen(false);
      return undefined;
    }

    if (!window.visualViewport) {
      return undefined;
    }

    baselineInnerHeightRef.current = window.innerHeight;

    function clearSettleTimers() {
      settleTimersRef.current.forEach(id => window.clearTimeout(id));
      settleTimersRef.current = [];
    }

    function resetStyles(el: HTMLElement) {
      el.style.bottom = "";
      if (lastAppliedRef.current !== null) {
        el.style.height = "";
        el.style.maxHeight = "";
        lastAppliedRef.current = null;
      }
      el.removeAttribute("data-keyboard-open");
    }

    function closeKeyboard(el: HTMLElement) {
      if (!keyboardOpenRef.current) {
        return;
      }
      clearSettleTimers();
      keyboardOpenRef.current = false;
      setKeyboardOpen(false);
      resetStyles(el);
      scrollTouchRef.current = null;
    }

    function scrollFocusedFieldIntoView(el: HTMLElement) {
      const focused = document.activeElement;
      if (!(focused instanceof HTMLElement) || !el.contains(focused) || !isKeyboardInput(focused)) {
        return;
      }
      const scroll = el.querySelector(".ui-bottom-sheet-dialog-scroll");
      if (!(scroll instanceof HTMLElement)) {
        return;
      }
      const scrollRect = scroll.getBoundingClientRect();
      const focusRect = focused.getBoundingClientRect();
      const pad = 24;
      if (focusRect.bottom > scrollRect.bottom - pad) {
        scroll.scrollTop += focusRect.bottom - scrollRect.bottom + pad;
      } else if (focusRect.top < scrollRect.top + pad) {
        scroll.scrollTop -= scrollRect.top - focusRect.top + pad;
      }
    }

    function writeLayout(el: HTMLElement, bottom: number, nextHeight: number, available: number, vvHeight: number) {
      const prev = lastAppliedRef.current;
      if (prev && prev.bottom === bottom && prev.height === nextHeight && prev.vvHeight === vvHeight) {
        return;
      }
      el.style.bottom = `${bottom}px`;
      el.style.height = `${nextHeight}px`;
      el.style.maxHeight = `${available}px`;
      lastAppliedRef.current = { bottom, height: nextHeight, vvHeight };
      requestAnimationFrame(() => scrollFocusedFieldIntoView(el));
    }

    function applyKeyboardLayout(el: HTMLElement, mode: "open" | "resize" | "settle") {
      window.scrollTo(0, 0);

      const metrics = getKeyboardMetrics(baselineInnerHeightRef.current || window.innerHeight);
      if (!metrics) {
        return;
      }

      const { vvHeight, keyboardHeight } = metrics;
      if (keyboardHeight <= KEYBOARD_RESIZE_THRESHOLD) {
        closeKeyboard(el);
        return;
      }

      const focused = document.activeElement;
      const focusedInSheet = isKeyboardInput(focused) && el.contains(focused);
      if (!focusedInSheet) {
        return;
      }

      const opening = !keyboardOpenRef.current;
      if (opening) {
        keyboardOpenRef.current = true;
        setKeyboardOpen(true);
        el.setAttribute("data-keyboard-open", "");
      }

      if (!opening && mode === "resize" && lastAppliedRef.current) {
        const vvDelta = Math.abs(vvHeight - lastAppliedRef.current.vvHeight);
        if (vvDelta < 8) {
          return;
        }
      }

      const available = Math.max(vvHeight - WINDOW_TOP_OFFSET, 0);
      writeLayout(el, keyboardHeight, available, available, vvHeight);
    }

    function scheduleSettle(el: HTMLElement) {
      clearSettleTimers();
      for (const delay of [50, 150, 300]) {
        const id = window.setTimeout(() => {
          if (keyboardOpenRef.current && popupRef.current === el) {
            applyKeyboardLayout(el, "settle");
          }
        }, delay);
        settleTimersRef.current.push(id);
      }
    }

    function onVisualViewportResize() {
      const el = popupRef.current;
      if (!el) {
        return;
      }
      const focused = document.activeElement;
      const focusedInSheet = isKeyboardInput(focused) && el.contains(focused);
      if (!focusedInSheet && !keyboardOpenRef.current) {
        return;
      }
      applyKeyboardLayout(el, keyboardOpenRef.current ? "resize" : "open");
      if (keyboardOpenRef.current) {
        scheduleSettle(el);
      }
    }

    function onTouchStart(event: TouchEvent) {
      const el = popupRef.current;
      const touch = event.touches[0];
      gestureMovedRef.current = false;
      if (!el || !touch || !el.contains(event.target as Node)) {
        scrollTouchRef.current = null;
        return;
      }
      if (!keyboardOpenRef.current) {
        const focused = document.activeElement;
        if (isKeyboardInput(focused) && el.contains(focused)) {
          applyKeyboardLayout(el, "open");
        }
      }
      if (!keyboardOpenRef.current) {
        scrollTouchRef.current = null;
        return;
      }
      scrollTouchRef.current = {
        scrollable: findSheetScrollTarget(event.target, el),
        lastY: touch.clientY,
      };
    }

    function onTouchMove(event: TouchEvent) {
      if (!keyboardOpenRef.current) {
        return;
      }
      const touch = event.touches[0];
      const state = scrollTouchRef.current;
      if (!touch || !state) {
        return;
      }

      const dy = touch.clientY - state.lastY;
      if (Math.abs(dy) > TAP_MOVE_THRESHOLD) {
        gestureMovedRef.current = true;
      }
      state.lastY = touch.clientY;

      event.preventDefault();

      const { scrollable } = state;
      if (!scrollable) {
        return;
      }
      const maxScroll = scrollable.scrollHeight - scrollable.clientHeight;
      if (maxScroll <= 0) {
        return;
      }
      scrollable.scrollTop = Math.max(0, Math.min(maxScroll, scrollable.scrollTop - dy));
    }

    function onTouchEnd() {
      scrollTouchRef.current = null;
    }

    function onPointerDown(event: PointerEvent) {
      if (event.button !== 0) {
        return;
      }
      gestureMovedRef.current = false;
      tapStartRef.current = { x: event.clientX, y: event.clientY };
    }

    function onPointerUp(event: PointerEvent) {
      const start = tapStartRef.current;
      tapStartRef.current = null;
      if (!start || !keyboardOpenRef.current || gestureMovedRef.current) {
        return;
      }
      if (
        Math.abs(event.clientX - start.x) > TAP_MOVE_THRESHOLD ||
        Math.abs(event.clientY - start.y) > TAP_MOVE_THRESHOLD
      ) {
        return;
      }
      const el = popupRef.current;
      if (!el || !el.contains(event.target as Node)) {
        return;
      }
      if (event.target instanceof Element) {
        const field = event.target.closest(
          "input, textarea, [contenteditable='true'], [contenteditable='']",
        );
        if (field && isKeyboardInput(field)) {
          return;
        }
      }
      const active = document.activeElement;
      if (isKeyboardInput(active) && el.contains(active)) {
        active.blur();
      }
    }

    function onPointerCancel() {
      tapStartRef.current = null;
      scrollTouchRef.current = null;
    }

    function onFocusIn() {
      const el = popupRef.current;
      if (!el) {
        return;
      }
      if (isKeyboardInput(document.activeElement) && el.contains(document.activeElement)) {
        applyKeyboardLayout(el, "open");
        scheduleSettle(el);
      }
    }

    function onFocusOut() {
      requestAnimationFrame(() => {
        const el = popupRef.current;
        if (!el) {
          return;
        }
        if (isKeyboardInput(document.activeElement) && el.contains(document.activeElement)) {
          return;
        }
        const metrics = getKeyboardMetrics(baselineInnerHeightRef.current || window.innerHeight);
        if (!metrics || metrics.keyboardHeight <= KEYBOARD_RESIZE_THRESHOLD) {
          closeKeyboard(el);
        }
      });
    }

    function onVisualViewportScroll() {
      if (keyboardOpenRef.current) {
        window.scrollTo(0, 0);
      }
    }

    const visualViewport = window.visualViewport;
    visualViewport.addEventListener("resize", onVisualViewportResize);
    visualViewport.addEventListener("scroll", onVisualViewportScroll);
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", onFocusOut, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("pointerup", onPointerUp, true);
    document.addEventListener("pointercancel", onPointerCancel, true);
    document.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
    document.addEventListener("touchmove", onTouchMove, { capture: true, passive: false });
    document.addEventListener("touchend", onTouchEnd, true);
    document.addEventListener("touchcancel", onTouchEnd, true);

    return () => {
      clearSettleTimers();
      visualViewport.removeEventListener("resize", onVisualViewportResize);
      visualViewport.removeEventListener("scroll", onVisualViewportScroll);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", onFocusOut, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("pointerup", onPointerUp, true);
      document.removeEventListener("pointercancel", onPointerCancel, true);
      document.removeEventListener("touchstart", onTouchStart, true);
      document.removeEventListener("touchmove", onTouchMove, true);
      document.removeEventListener("touchend", onTouchEnd, true);
      document.removeEventListener("touchcancel", onTouchEnd, true);
      const el = popupRef.current;
      if (el && keyboardOpenRef.current) {
        resetStyles(el);
      }
      keyboardOpenRef.current = false;
      setKeyboardOpen(false);
    };
  }, [enabled, mounted, open, popupRef, setKeyboardOpen]);
}
